// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * The app's react-query client (ADR-0008).
 *
 * Built in its own module so tests can construct an isolated client with the
 * same defaults instead of sharing the app singleton's cache.
 */

import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '../services/api';
import { QUERY_GC_TIME_MS, QUERY_STALE_TIME_MS } from './queryConfig';

/** Retries at most twice, and never for a response the server refused outright. */
const MAX_RETRIES = 2;

/**
 * `services/api.ts` drives the session lock through zustand on a 401, so
 * react-query's default 3× retry would fire that path four times. A 4xx is the
 * server's answer, not a transient fault — only 5xx and genuine network errors
 * (no status) are worth retrying.
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status !== undefined && error.status < 500) {
    return false;
  }
  return failureCount < MAX_RETRIES;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: QUERY_STALE_TIME_MS,
        gcTime: QUERY_GC_TIME_MS,
        retry: shouldRetry,
        // Polls exist to keep an open screen fresh; a hidden tab has no screen.
        refetchIntervalInBackground: false,
        // Off deliberately: none of the hand-rolled hooks this layer replaces
        // refetched on focus, and switching it on would add traffic (including
        // multi-megabyte twin graphs) that nothing in ADR-0008 asked for.
        refetchOnWindowFocus: false,
      },
    },
  });
}

export const queryClient = createQueryClient();
